import type { ActivityOverviewSupportDto } from '../../../../core/api/FitnessActivityClient';
import {
    ActivityType,
    SupportedFitnessApp,
    FacilityVisitType,
    WorkoutTypeV2
} from '../../../../core/api/FitnessActivityClient';

export const UNKNOWN_SUBTYPE = 'UNKNOWN';

export type ActivityFilterSubType = string | number;

export type ActivityFilterItem = {
    category: ActivityType;
    subType: ActivityFilterSubType;
    label: string;
};

export type ActivityFilterOption = ActivityFilterItem & {
    key: string;
};

export const categoryLabels: Record<ActivityType, string> = {
    [ActivityType.DAILY_STEPS]: 'Daily Steps',
    [ActivityType.FACILITY_VISIT]: 'Facility Visit',
    [ActivityType.WORKOUT]: 'Workout'
};

export const fitnessAppLabels: Record<SupportedFitnessApp, string> = {
    [SupportedFitnessApp.STRAVA]: 'Strava',
    [SupportedFitnessApp.GARMIN]: 'Garmin',
    [SupportedFitnessApp.FITBIT]: 'Fitbit',
    [SupportedFitnessApp.POLAR]: 'Polar',
    [SupportedFitnessApp.MYSPORTS_REWARDS]: 'MySports Rewards',
    [SupportedFitnessApp.MOVE_REPUBLIC]: 'Move Republic',
    [SupportedFitnessApp.MOVE_REPUBLIC_ADMIN]: 'Move Republic Admin',
    [SupportedFitnessApp.GOOGLE_FIT]: 'Google Fit',
    [SupportedFitnessApp.HEALTH_CONNECT]: 'Health Connect',
    [SupportedFitnessApp.WITHINGS]: 'Withings',
    [SupportedFitnessApp.APPLE_HEALTH]: 'Apple Health',
    [SupportedFitnessApp.HUAWEI_HEALTH]: 'Huawei',
    [SupportedFitnessApp.SAMSUNG_HEALTH]: 'Samsung Health',
    [SupportedFitnessApp.SUUNTO]: 'Suunto',
    [SupportedFitnessApp.ZEPP]: 'Zepp',
    [SupportedFitnessApp.GOOGLE_HEALTH]: 'Google Health'
};

export const facilityVisitLabels: Record<FacilityVisitType, string> = {
    [FacilityVisitType.MANUAL]: 'Manual Check-in',
    [FacilityVisitType.AUTOMATIC]: 'Automatic Check-in',
    [FacilityVisitType.PUBLIC_FACILITY]: 'Public Facility',
    [FacilityVisitType.PRIVATE_FACILITY]: 'Private Facility'
};

export const getWorkoutLabel = (workoutType?: WorkoutTypeV2): string => {
    if (!workoutType) return 'Unknown';

    const typeString = workoutType.toString().toLowerCase();
    return typeString
        .split('_')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
};

export const activityKey = (
    category: ActivityType,
    subType: ActivityFilterSubType
) => `${category}:${String(subType)}`;

export const buildActivityOptions = (
    dayDataArray: Array<{ data: ActivityOverviewSupportDto | null }>
): ActivityFilterOption[] => {
    const activities = new Map<string, ActivityFilterOption>();

    dayDataArray.forEach((dayData) => {
        if (!dayData.data) return;

        dayData.data.dailySteps.forEach((step) => {
            if (step.fitnessApp === null || step.fitnessApp === undefined) {
                const subType = UNKNOWN_SUBTYPE;
                const label = `${categoryLabels[ActivityType.DAILY_STEPS]} - Unknown`;
                const key = activityKey(ActivityType.DAILY_STEPS, subType);
                activities.set(key, {
                    category: ActivityType.DAILY_STEPS,
                    subType,
                    label,
                    key
                });
                return;
            }

            const subType = step.fitnessApp;
            const subLabel =
                fitnessAppLabels[step.fitnessApp] || String(step.fitnessApp);
            const label = `${categoryLabels[ActivityType.DAILY_STEPS]} - ${subLabel}`;
            const key = activityKey(ActivityType.DAILY_STEPS, subType);
            activities.set(key, {
                category: ActivityType.DAILY_STEPS,
                subType,
                label,
                key
            });
        });

        dayData.data.facilityVisits.forEach((visit) => {
            if (visit.visitType === null || visit.visitType === undefined) {
                const subType = UNKNOWN_SUBTYPE;
                const label = `${categoryLabels[ActivityType.FACILITY_VISIT]} - Unknown`;
                const key = activityKey(ActivityType.FACILITY_VISIT, subType);
                activities.set(key, {
                    category: ActivityType.FACILITY_VISIT,
                    subType,
                    label,
                    key
                });
                return;
            }

            const subType = visit.visitType;
            const subLabel =
                facilityVisitLabels[visit.visitType] || String(visit.visitType);
            const label = `${categoryLabels[ActivityType.FACILITY_VISIT]} - ${subLabel}`;
            const key = activityKey(ActivityType.FACILITY_VISIT, subType);
            activities.set(key, {
                category: ActivityType.FACILITY_VISIT,
                subType,
                label,
                key
            });
        });

        dayData.data.workouts.forEach((workout) => {
            if (
                workout.workoutType === null ||
                workout.workoutType === undefined
            ) {
                const subType = UNKNOWN_SUBTYPE;
                const label = `${categoryLabels[ActivityType.WORKOUT]} - Unknown`;
                const key = activityKey(ActivityType.WORKOUT, subType);
                activities.set(key, {
                    category: ActivityType.WORKOUT,
                    subType,
                    label,
                    key
                });
                return;
            }

            const subType = workout.workoutType;
            const subLabel = getWorkoutLabel(workout.workoutType);
            const label = `${categoryLabels[ActivityType.WORKOUT]} - ${subLabel}`;
            const key = activityKey(ActivityType.WORKOUT, subType);
            activities.set(key, {
                category: ActivityType.WORKOUT,
                subType,
                label,
                key
            });
        });
    });

    return Array.from(activities.values());
};

export const pruneSelectedActivities = (
    selectedActivities: ActivityFilterItem[],
    availableOptions: ActivityFilterOption[]
) => {
    const availableKeys = new Set(availableOptions.map((option) => option.key));
    return selectedActivities.filter((activity) =>
        availableKeys.has(activityKey(activity.category, activity.subType))
    );
};
